/**
 * Production-Ready Logging Utility
 * Provides structured logging with different levels, context, and remote logging support
 */

import * as Sentry from '@sentry/react';
import { getSafeEnvironmentConfig } from './environmentCheck';

const isTestEnv =
  typeof process !== 'undefined' &&
  (process.env['NODE_ENV'] === 'test' || process.env['VITEST'] !== undefined);

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4
}

export interface LogContext {
  userId?: string;
  establishmentId?: string;
  sessionId?: string;
  module?: string;
  action?: string;
  metadata?: Record<string, unknown>;
}

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: LogContext;
  error?: Error;
  stackTrace?: string;
  userAgent?: string;
  url?: string;
  performance?: {
    duration?: number;
    memoryUsage?: number;
  };
}

export interface LoggerConfig {
  level: LogLevel;
  enableConsole: boolean;
  enableRemote: boolean;
  enableLocalStorage: boolean;
  maxLocalStorageEntries: number;
  remoteEndpoint?: string;
  batchSize: number;
  flushInterval: number;
  enablePerformanceMetrics: boolean;
  enableErrorReporting: boolean;
  sensitiveFields: string[];
}

class Logger {
  private config: LoggerConfig;
  private logBuffer: LogEntry[] = [];
  private flushTimer?: NodeJS.Timeout;
  private sessionId: string;

  constructor(config?: Partial<LoggerConfig>) {
    this.config = {
      level: this.getLogLevelFromEnv(),
      enableConsole: !this.isProduction() || this.isDebugMode(),
      enableRemote: this.isProduction(),
      enableLocalStorage: true,
      maxLocalStorageEntries: 100,
      remoteEndpoint: import.meta.env['VITE_LOG_ENDPOINT'],
      batchSize: 50,
      flushInterval: 30000, // 30 seconds
      enablePerformanceMetrics: import.meta.env['VITE_ENABLE_PERFORMANCE_MONITORING'] === 'true',
      enableErrorReporting: import.meta.env['VITE_ENABLE_ERROR_REPORTING'] === 'true',
      sensitiveFields: ['password', 'token', 'secret', 'api_key', 'apiKey', 'authorization'],
      ...config
    };

    this.sessionId = this.generateSessionId();
    if (!isTestEnv) {
      this.startFlushTimer();
      this.setupErrorHandlers();
    }
  }

  /**
   * Log methods for different levels
   */
  debug(message: string, context?: LogContext): void {
    this.log(LogLevel.DEBUG, message, context);
  }

  info(message: string, context?: LogContext): void {
    this.log(LogLevel.INFO, message, context);
  }

  warn(message: string, context?: LogContext): void {
    this.log(LogLevel.WARN, message, context);
  }

  error(message: string, error?: Error | LogContext, context?: LogContext): void {
    if (error instanceof Error) {
      this.log(LogLevel.ERROR, message, context, error);
    } else {
      this.log(LogLevel.ERROR, message, error as LogContext);
    }
  }

  /**
   * Performance logging
   */
  performance(action: string, duration: number, context?: LogContext): void {
    if (!this.config.enablePerformanceMetrics) return;

    const perfContext: LogContext = {
      ...context,
      action,
      metadata: {
        ...context?.metadata,
        duration,
        timestamp: new Date().toISOString()
      }
    };

    this.log(LogLevel.INFO, `Performance: ${action} took ${duration}ms`, perfContext);
  }

  /**
   * API request/response logging
   */
  apiRequest(method: string, url: string, context?: LogContext): void {
    this.debug(`API Request: ${method} ${url}`, {
      ...context,
      module: 'api',
      action: 'request',
      metadata: { method, url }
    });
  }

  apiResponse(method: string, url: string, status: number, duration: number, context?: LogContext): void {
    const level = status >= 400 ? LogLevel.ERROR : LogLevel.DEBUG;
    this.log(level, `API Response: ${method} ${url} - ${status} (${duration}ms)`, {
      ...context,
      module: 'api',
      action: 'response',
      metadata: { method, url, status, duration }
    });
  }

  /**
   * User action logging
   */
  userAction(action: string, details?: Record<string, unknown>, context?: LogContext): void {
    this.info(`User Action: ${action}`, {
      ...context,
      action,
      metadata: { ...context?.metadata, ...details }
    });
  }

  /**
   * Security event logging
   */
  securityEvent(event: string, details?: Record<string, unknown>, context?: LogContext): void {
    this.warn(`Security Event: ${event}`, {
      ...context,
      module: 'security',
      action: event,
      metadata: { ...context?.metadata, ...details }
    });
  }

  /**
   * Main logging method
   */
  private log(level: LogLevel, message: string, context?: LogContext, error?: Error): void {
    if (level < this.config.level) return;

    const logEntry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message: this.sanitizeMessage(message),
      context: this.sanitizeContext(context),
      error: error ? this.sanitizeError(error) : undefined,
      stackTrace: error?.stack,
      userAgent: navigator.userAgent,
      url: window.location.href,
      performance: this.config.enablePerformanceMetrics ? this.getPerformanceMetrics() : undefined
    };

    // Console logging
    if (this.config.enableConsole) {
      this.logToConsole(logEntry);
    }

    // Local storage logging
    if (this.config.enableLocalStorage) {
      this.logToLocalStorage(logEntry);
    }

    // Buffer for remote logging
    if (this.config.enableRemote) {
      this.bufferLog(logEntry);
    }

    // Send to error reporting service for errors
    if (level === LogLevel.ERROR && this.config.enableErrorReporting) {
      this.reportError(logEntry);
    }
  }

  /**
   * Console logging with proper formatting
   */
  private logToConsole(entry: LogEntry): void {
    const timestamp = new Date(entry.timestamp).toLocaleTimeString();
    const prefix = `[${timestamp}] [${LogLevel[entry.level]}]`;
    const style = this.getConsoleStyle(entry.level);

    console.log(`%c${prefix}%c ${entry.message}`, style, 'color: inherit');

    if (entry.context && Object.keys(entry.context).length > 0) {
      console.log('Context:', entry.context);
    }

    if (entry.error) {
      console.error('Error:', entry.error);
    }

    if (entry.performance) {
      console.log('Performance:', entry.performance);
    }
  }

  /**
   * Get console style based on log level
   */
  private getConsoleStyle(level: LogLevel): string {
    switch (level) {
      case LogLevel.DEBUG:
        return 'color: #888';
      case LogLevel.INFO:
        return 'color: #2196F3';
      case LogLevel.WARN:
        return 'color: #FF9800';
      case LogLevel.ERROR:
        return 'color: #F44336; font-weight: bold';
      default:
        return 'color: inherit';
    }
  }

  /**
   * Local storage logging for debugging
   */
  private logToLocalStorage(entry: LogEntry): void {
    try {
      const storageKey = 'mep_logs';
      const existingLogs = JSON.parse(localStorage.getItem(storageKey) || '[]');
      
      existingLogs.push(entry);

      // Keep only recent logs
      while (existingLogs.length > this.config.maxLocalStorageEntries) {
        existingLogs.shift();
      }

      localStorage.setItem(storageKey, JSON.stringify(existingLogs));
    } catch {
      // Ignore localStorage errors (quota exceeded, etc.)
    }
  }

  /**
   * Buffer logs for batch remote sending
   */
  private bufferLog(entry: LogEntry): void {
    this.logBuffer.push(entry);

    if (this.logBuffer.length >= this.config.batchSize) {
      this.flush();
    }
  }

  /**
   * Send buffered logs to remote endpoint
   */
  private async flush(): Promise<void> {
    if (this.logBuffer.length === 0 || !this.config.remoteEndpoint) return;

    const logsToSend = [...this.logBuffer];
    this.logBuffer = [];

    try {
      const response = await fetch(this.config.remoteEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-ID': this.sessionId
        },
        body: JSON.stringify({
          logs: logsToSend,
          environment: this.getEnvironmentInfo()
        })
      });

      if (!response.ok) {
        // Re-buffer logs if sending failed
        this.logBuffer.unshift(...logsToSend);
      }
    } catch (error) {
      // Re-buffer logs if sending failed
      this.logBuffer.unshift(...logsToSend);
      console.error('Failed to send logs to remote endpoint:', error);
    }
  }

  /**
   * Start timer for periodic log flushing
   */
  private startFlushTimer(): void {
    if (this.config.enableRemote && this.config.flushInterval > 0) {
      this.flushTimer = setInterval(() => {
        this.flush();
      }, this.config.flushInterval);
    }
  }

  /**
   * Setup global error handlers
   */
  private setupErrorHandlers(): void {
    // Handle unhandled errors
    window.addEventListener('error', (event) => {
      this.error('Unhandled error', new Error(event.message), {
        module: 'global',
        action: 'error',
        metadata: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno
        }
      });
    });

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.error('Unhandled promise rejection', new Error(event.reason), {
        module: 'global',
        action: 'unhandledRejection'
      });
    });
  }

  /**
   * Report error to Sentry
   */
  private reportError(entry: LogEntry): void {
    Sentry.captureException(entry.error ?? new Error(entry.message), {
      tags: {
        module: entry.context?.module,
        action: entry.context?.action,
      },
      extra: {
        context: entry.context,
        performance: entry.performance,
      },
    });
  }

  /**
   * Sanitize message to remove sensitive data
   */
  private sanitizeMessage(message: string): string {
    let sanitized = message;
    
    this.config.sensitiveFields.forEach(field => {
      const regex = new RegExp(`(${field})[\\s:=]*(["']?)([^"'\\s]+)(["']?)`, 'gi');
      sanitized = sanitized.replace(regex, '$1$2***$4');
    });

    return sanitized;
  }

  /**
   * Sanitize context to remove sensitive data
   */
  private sanitizeContext(context?: LogContext): LogContext | undefined {
    if (!context) return undefined;

    const sanitized = { ...context };
    
    if (sanitized.metadata) {
      sanitized.metadata = this.sanitizeObject(sanitized.metadata);
    }

    return sanitized;
  }

  /**
   * Sanitize object recursively
   */
  private sanitizeObject(obj: Record<string, unknown>): Record<string, unknown> {
    const sanitized: Record<string, unknown> = {};
    
    Object.entries(obj).forEach(([key, value]) => {
      if (this.config.sensitiveFields.some(field => key.toLowerCase().includes(field))) {
        sanitized[key] = '***';
      } else if (typeof value === 'object' && value !== null) {
        sanitized[key] = this.sanitizeObject(value as Record<string, unknown>);
      } else {
        sanitized[key] = value;
      }
    });

    return sanitized;
  }

  /**
   * Sanitize error object
   */
  private sanitizeError(error: Error): Error {
    const sanitized = new Error(this.sanitizeMessage(error.message));
    sanitized.name = error.name;
    sanitized.stack = error.stack;
    return sanitized;
  }

  /**
   * Get performance metrics
   */
  private getPerformanceMetrics(): { duration?: number; memoryUsage?: number } {
    const metrics: { duration?: number; memoryUsage?: number } = {};

    if (performance && performance.now) {
      metrics.duration = performance.now();
    }

    const performanceWithMemory = performance as unknown as { 
      memory?: { usedJSHeapSize?: number } 
    };
    
    if (performanceWithMemory.memory?.usedJSHeapSize) {
      metrics.memoryUsage = performanceWithMemory.memory.usedJSHeapSize;
    }

    return metrics;
  }

  /**
   * Get environment information
   */
  private getEnvironmentInfo(): Record<string, unknown> {
    return {
      ...getSafeEnvironmentConfig(),
      sessionId: this.sessionId,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      screen: {
        width: window.screen.width,
        height: window.screen.height
      },
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    };
  }

  /**
   * Generate unique session ID
   */
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get log level from environment
   */
  private getLogLevelFromEnv(): LogLevel {
    const level = import.meta.env['VITE_LOG_LEVEL']?.toLowerCase();
    
    switch (level) {
      case 'debug':
        return LogLevel.DEBUG;
      case 'info':
        return LogLevel.INFO;
      case 'warn':
        return LogLevel.WARN;
      case 'error':
        return LogLevel.ERROR;
      case 'none':
        return LogLevel.NONE;
      default:
        return this.isProduction() ? LogLevel.ERROR : LogLevel.DEBUG;
    }
  }

  /**
   * Check if running in production
   */
  private isProduction(): boolean {
    return import.meta.env.MODE === 'production';
  }

  /**
   * Check if debug mode is enabled
   */
  private isDebugMode(): boolean {
    return localStorage.getItem('debug') === 'true' || 
           import.meta.env['VITE_DEBUG'] === 'true';
  }

  /**
   * Get current logger configuration
   */
  getConfig(): LoggerConfig {
    return { ...this.config };
  }

  /**
   * Update logger configuration
   */
  updateConfig(config: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...config };
    
    // Restart flush timer if interval changed
    if (config.flushInterval !== undefined || config.enableRemote !== undefined) {
      if (this.flushTimer) {
        clearInterval(this.flushTimer);
      }
      this.startFlushTimer();
    }
  }

  /**
   * Force flush all buffered logs
   */
  async forceFlush(): Promise<void> {
    await this.flush();
  }

  /**
   * Clear local storage logs
   */
  clearLocalLogs(): void {
    localStorage.removeItem('mep_logs');
  }

  /**
   * Get logs from local storage
   */
  getLocalLogs(): LogEntry[] {
    try {
      return JSON.parse(localStorage.getItem('mep_logs') || '[]');
    } catch {
      return [];
    }
  }

  /**
   * Export logs for debugging
   */
  exportLogs(): string {
    const logs = this.getLocalLogs();
    return JSON.stringify(logs, null, 2);
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    this.flush();
  }
}

// Create and export singleton instance
export const logger = new Logger();

// Export for testing
export { Logger };
